import {
	getAnalysisKeyModificationSummary,
	getKeyModificationSummaryLines,
} from "./keyModificationSummary";

const CANVAS_HELP_URL =
	"https://support.eclass.ualberta.ca/Knowledgebase/Article/View/580/55/importing-grades-into-canvas";

export const EMAIL_TEMPLATE_PRESETS = [
	{
		value: "pickup",
		label: "Pickup",
		deliveryText:
			"The corresponding Scantron sheets are available for pickup at your earliest convenience.",
	},
	{
		value: "campus_mail",
		label: "Campus Mail",
		deliveryText:
			"The corresponding Scantron sheets will be delivered back to you via campus mail.",
	},
	{
		value: "courier",
		label: "Courier",
		deliveryText: "The corresponding Scantron sheets will be returned to you shortly via courier.",
	},
];

const PRESET_MAP = EMAIL_TEMPLATE_PRESETS.reduce((acc, preset) => {
	acc[preset.value] = preset;
	return acc;
}, {});

const POINT_BISERIAL_LABELS = {
	crpb: "CRPB",
	legacy_crpb: "Legacy CRPB",
	rpb: "RPB",
};

const ITEM_ANALYSIS_THRESHOLDS = {
	tooHard: 0.3,
	tooEasy: 0.9,
	negativeDiscrimination: 0,
	weakDiscrimination: 0.15,
	highBlankRate: 0.1,
	positiveDistractorDp: 0.1,
};

const MATCH_TYPE_GROUPS = {
	exact: new Set(["exact-id-and-name"]),
	close: new Set([
		"exact-id-fuzzy-name",
		"exact-id-partial-name",
		"exact-id-and-one-name",
		"exact-id-last-token",
	]),
	cannot: new Set(["cannot-match", "no-match"]),
};

function fallbackName(senderName) {
	const trimmed = String(senderName || "").trim();
	return trimmed || "TSQS";
}

function toFiniteNumber(value) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : null;
}

function formatFixed(value, decimals = 1) {
	const numericValue = toFiniteNumber(value);
	return numericValue == null ? "" : numericValue.toFixed(decimals);
}

function formatPercent(value, decimals = 0) {
	const numericValue = toFiniteNumber(value);
	return numericValue == null ? "" : `${(numericValue * 100).toFixed(decimals)}%`;
}

function pluralize(count, singular, plural = `${singular}s`) {
	return Number(count) === 1 ? singular : plural;
}

function compareVersionCodes(a, b) {
	const left = String(a ?? "").trim();
	const right = String(b ?? "").trim();
	const leftNumeric = /^\d+$/.test(left);
	const rightNumeric = /^\d+$/.test(right);
	if (leftNumeric && rightNumeric) return Number(left) - Number(right);
	if (leftNumeric) return -1;
	if (rightNumeric) return 1;
	return left.localeCompare(right);
}

function joinExamples(items, formatter, limit = 3) {
	return (items || [])
		.slice(0, limit)
		.map((item) => formatter(item))
		.filter(Boolean)
		.join("; ");
}

function buildQuestionLabel(item, includeVersion = false) {
	const itemNo = Number(item?.itemNo);
	if (!Number.isFinite(itemNo)) return "";
	if (includeVersion && item?.version) {
		return `V${item.version} Q${itemNo}`;
	}
	return `Q${itemNo}`;
}

function buildStudentReference(student) {
	const name = String(student?.std_name_raw || student?.std_name_matched || "").trim();
	const id = String(student?.std_id_raw || student?.std_id_matched || "").trim();
	const rspId = String(student?.rsp_id || "").trim();

	if (name && id) return `${name} (${id})`;
	if (name) return name;
	if (id) return id;
	if (rspId) return `Scanner record ${rspId}`;
	return "Scanner record";
}

function normalizeAnalysesByVersion(analysesByVersion = []) {
	return (analysesByVersion || [])
		.map((entry) => {
			const analysis = entry?.analysis || {};
			const studentCount = Math.max(0, Number(analysis?.student_count || 0));
			const meanTotal = toFiniteNumber(analysis?.mean_total);
			const varianceTotal = toFiniteNumber(analysis?.var_total);
			const sdTotal = toFiniteNumber(analysis?.sd_total);
			const kr20 = toFiniteNumber(analysis?.kr20);
			const kEffective = toFiniteNumber(analysis?.k_effective);
			const discardedBlankCount = Math.max(0, Number(analysis?.discarded_blank_count || 0));
			return {
				version: String(entry?.version ?? "").trim() || "1",
				analysis,
				studentCount,
				meanTotal,
				varianceTotal,
				sdTotal,
				kr20,
				kEffective,
				discardedBlankCount,
			};
		})
		.filter((entry) => entry.studentCount > 0)
		.sort((left, right) => compareVersionCodes(left.version, right.version));
}

function buildOverallPerformanceSummary(normalizedAnalyses) {
	if (!normalizedAnalyses.length) return null;

	const totalStudents = normalizedAnalyses.reduce((sum, entry) => sum + entry.studentCount, 0);
	if (!totalStudents) return null;

	const weightedMean =
		normalizedAnalyses.reduce(
			(sum, entry) => sum + entry.studentCount * Number(entry.meanTotal || 0),
			0,
		) / totalStudents;
	const pooledVariance =
		normalizedAnalyses.reduce((sum, entry) => {
			const mean = Number(entry.meanTotal || 0);
			const variance = Number(entry.varianceTotal || 0);
			return sum + entry.studentCount * (variance + (mean - weightedMean) ** 2);
		}, 0) / totalStudents;
	const pooledSd = Math.sqrt(Math.max(pooledVariance, 0));
	const discardedBlankCount = normalizedAnalyses.reduce(
		(sum, entry) => sum + entry.discardedBlankCount,
		0,
	);
	const uniqueEffectiveTotals = Array.from(
		new Set(
			normalizedAnalyses
				.map((entry) => entry.kEffective)
				.filter((value) => Number.isFinite(value) && value > 0),
		),
	);
	const scoreSuffix =
		uniqueEffectiveTotals.length === 1 ? `/${Number(uniqueEffectiveTotals[0])}` : "";

	return {
		totalStudents,
		versionCount: normalizedAnalyses.length,
		weightedMean,
		pooledSd,
		scoreSuffix,
		discardedBlankCount,
	};
}

function buildReliabilityValue(normalizedAnalyses) {
	const krValues = normalizedAnalyses
		.map((entry) => ({
			version: entry.version,
			kr20: entry.kr20,
		}))
		.filter((entry) => entry.kr20 != null);

	if (!krValues.length) return "";

	if (krValues.length === 1) {
		const [{ kr20 }] = krValues;
		return formatFixed(kr20, 3);
	}

	return krValues
		.map((entry) => `V${entry.version} ${formatFixed(entry.kr20, 3)}`)
		.join(", ");
}

function buildVersionMeanSpreadValue(normalizedAnalyses) {
	if (normalizedAnalyses.length < 2) return "";
	const means = normalizedAnalyses
		.map((entry) => entry.meanTotal)
		.filter((value) => Number.isFinite(value));
	if (!means.length) return "";

	const maxMean = Math.max(...means);
	const minMean = Math.min(...means);
	const spread = maxMean - minMean;

	return `${formatFixed(spread, 1)} point${Number(spread.toFixed(1)) === 1 ? "" : "s"}`;
}

function hasAnyMatchingData(students = []) {
	return (students || []).some(
		(student) =>
			student?.match_attempted === "Y" ||
			student?.std_id_matched ||
			student?.std_name_matched ||
			student?.ccid,
	);
}

function getCompletelyUnmatchedStudents(students = []) {
	return (students || []).filter(
		(student) =>
			((student?.match_attempted === "Y" &&
				MATCH_TYPE_GROUPS.cannot.has(String(student?.match_type || "").trim())) ||
				!student?.match_attempted) &&
			(!student?.std_id_matched || student.std_id_matched === "") &&
			(!student?.std_name_matched || student.std_name_matched === ""),
	);
}

function buildKeyModificationNoteRows(normalizedAnalyses = []) {
	const includeVersion = normalizedAnalyses.length > 1;

	return normalizedAnalyses.flatMap((entry) => {
		const summary = getAnalysisKeyModificationSummary(entry?.analysis);
		const lines = getKeyModificationSummaryLines(summary);
		if (!lines.length) return [];

		return [
			{
				note: includeVersion
					? `Answer Key Modifications (V${entry.version})`
					: "Answer Key Modifications",
				details: lines.join("; "),
			},
		];
	});
}

function getZeroScoreCount(normalizedAnalyses = []) {
	return normalizedAnalyses.reduce((total, entry) => {
		const rows = Array.isArray(entry?.analysis?.score_dist_table) ? entry.analysis.score_dist_table : [];
		return (
			total +
			rows.reduce((sectionTotal, row) => {
				const score = toFiniteNumber(row?.score);
				const freq = Math.max(0, Number(row?.freq || 0));
				if (score == null || Math.abs(score) > 1e-9 || freq <= 0) return sectionTotal;
				return sectionTotal + freq;
			}, 0)
		);
	}, 0);
}

function buildImportantNoteRows({
	normalizedAnalyses = [],
	students = [],
	gradeWithoutMatching = false,
	hasClassIds = false,
} = {}) {
	const rows = [...buildKeyModificationNoteRows(normalizedAnalyses)];
	const unmatchedStudents = getCompletelyUnmatchedStudents(students);
	const zeroScoreCount = getZeroScoreCount(normalizedAnalyses);

	if (
		unmatchedStudents.length > 0 &&
		hasAnyMatchingData(students) &&
		!gradeWithoutMatching &&
		hasClassIds
	) {
		const examples = joinExamples(unmatchedStudents, buildStudentReference, 3);
		rows.push({
			note: "Completely Unmatched Records",
			details: `${unmatchedStudents.length} scanner ${pluralize(
				unmatchedStudents.length,
				"record",
			)} remain completely unmatched to the class list${
				examples ? ` (${examples})` : ""
			}.`,
		});
	}

	if (zeroScoreCount > 0) {
		rows.push({
			note: "Zero Scores",
			details: `${zeroScoreCount} ${pluralize(
				zeroScoreCount,
				"student",
			)} scored 0 on the current scored question set.`,
		});
	}

	return rows;
}

function collectItemFlags(normalizedAnalyses, pointBiserialMode = "crpb") {
	const pbKey = ["crpb", "legacy_crpb", "rpb"].includes(pointBiserialMode)
		? pointBiserialMode
		: "crpb";
	const includeVersion = normalizedAnalyses.length > 1;
	const flags = {
		hasData: normalizedAnalyses.length > 0,
		pbLabel: POINT_BISERIAL_LABELS[pbKey] || POINT_BISERIAL_LABELS.crpb,
		negative: [],
		weak: [],
		hard: [],
		easy: [],
		blank: [],
		distractor: [],
	};

	normalizedAnalyses.forEach((entry) => {
		const headerLq = Number(entry?.analysis?.header?.lqnum || 1);
		const totalGroupN = Math.max(
			0,
			Number(entry?.analysis?.group_breakdown?.total?.n || entry.studentCount || 0),
		);
		const items = Array.isArray(entry?.analysis?.items) ? entry.analysis.items : [];
		const optionStats = entry?.analysis?.group_breakdown?.option_stats || {};
		const totalCountsByItem = entry?.analysis?.group_breakdown?.total?.counts || {};

		items.forEach((item) => {
			if (item?.removed) return;
			const itemIndex = Number(item?.index);
			if (!Number.isFinite(itemIndex)) return;
			const itemNo = headerLq + itemIndex - 1;
			const itemRef = { version: entry.version, itemNo };
			const difficulty = toFiniteNumber(item?.dif);
			const discrimination = toFiniteNumber(item?.[pbKey] ?? item?.crpb ?? item?.rpb);

			if (difficulty != null && difficulty <= ITEM_ANALYSIS_THRESHOLDS.tooHard) {
				flags.hard.push({
					...itemRef,
					difficulty,
					severity: ITEM_ANALYSIS_THRESHOLDS.tooHard - difficulty,
				});
			}
			if (difficulty != null && difficulty >= ITEM_ANALYSIS_THRESHOLDS.tooEasy) {
				flags.easy.push({
					...itemRef,
					difficulty,
					severity: difficulty - ITEM_ANALYSIS_THRESHOLDS.tooEasy,
				});
			}

			if (discrimination != null && discrimination < ITEM_ANALYSIS_THRESHOLDS.negativeDiscrimination) {
				flags.negative.push({
					...itemRef,
					discrimination,
					severity: Math.abs(discrimination),
				});
			} else if (
				discrimination != null &&
				discrimination < ITEM_ANALYSIS_THRESHOLDS.weakDiscrimination
			) {
				flags.weak.push({
					...itemRef,
					discrimination,
					severity: ITEM_ANALYSIS_THRESHOLDS.weakDiscrimination - discrimination,
				});
			}

			const totalCounts = totalCountsByItem?.[itemIndex] || {};
			const blankCount = Number(totalCounts?.[" "] || 0) + Number(totalCounts?.["*"] || 0);
			const blankRate = totalGroupN > 0 ? blankCount / totalGroupN : 0;
			if (blankRate > ITEM_ANALYSIS_THRESHOLDS.highBlankRate) {
				flags.blank.push({
					...itemRef,
					blankRate,
					severity: blankRate - ITEM_ANALYSIS_THRESHOLDS.highBlankRate,
				});
			}

			const correctOptions = new Set(item?.correct_options || []);
			const statsForItem = optionStats?.[itemIndex] || {};
			let bestDistractor = null;
			Object.entries(statsForItem).forEach(([option, stats]) => {
				if (correctOptions.has(option)) return;
				const dp = toFiniteNumber(stats?.dp);
				if (dp == null || dp <= ITEM_ANALYSIS_THRESHOLDS.positiveDistractorDp) return;
				if (!bestDistractor || dp > bestDistractor.dp) {
					bestDistractor = { option, dp };
				}
			});
			if (bestDistractor) {
				flags.distractor.push({
					...itemRef,
					option: bestDistractor.option,
					dp: bestDistractor.dp,
					severity: bestDistractor.dp - ITEM_ANALYSIS_THRESHOLDS.positiveDistractorDp,
				});
			}
		});
	});

	Object.values(flags).forEach((value) => {
		if (Array.isArray(value)) {
			value.sort((left, right) => {
				const severityGap = Number(right.severity || 0) - Number(left.severity || 0);
				if (severityGap !== 0) return severityGap;
				return buildQuestionLabel(left, includeVersion).localeCompare(
					buildQuestionLabel(right, includeVersion),
				);
			});
		}
	});

	flags.includeVersion = includeVersion;
	return flags;
}

function buildOverviewRows({
	normalizedAnalyses,
	overallSummary,
	selectedQuestionCount,
	totalQuestionCount,
} = {}) {
	const rows = [];

	if (
		Number.isFinite(selectedQuestionCount) &&
		Number.isFinite(totalQuestionCount) &&
		selectedQuestionCount > 0 &&
		totalQuestionCount > 0 &&
		selectedQuestionCount < totalQuestionCount
	) {
		rows.push({
			metric: "Questions Included",
			value: `${selectedQuestionCount} of ${totalQuestionCount}`,
		});
	} else if (
		Number.isFinite(selectedQuestionCount) &&
		selectedQuestionCount > 0 &&
		!Number.isFinite(totalQuestionCount)
	) {
		rows.push({
			metric: "Questions Included",
			value: `${selectedQuestionCount}`,
		});
	}

	if (!overallSummary) return rows;

	rows.push(
		{
			metric: "Processed Responses",
			value: `${overallSummary.totalStudents}`,
		},
		{
			metric: "Versions Processed",
			value: `${overallSummary.versionCount}`,
		},
		{
			metric: "Mean Score",
			value: `${formatFixed(overallSummary.weightedMean, 1)}${overallSummary.scoreSuffix}`,
		},
		{
			metric: "Standard Deviation",
			value: formatFixed(overallSummary.pooledSd, 1),
		},
	);

	const reliabilityValue = buildReliabilityValue(normalizedAnalyses);
	if (reliabilityValue) {
		rows.push({
			metric: "KR-20 Reliability",
			value: reliabilityValue,
		});
	}

	const spreadValue = buildVersionMeanSpreadValue(normalizedAnalyses);
	if (spreadValue) {
		rows.push({
			metric: "Version Mean Spread",
			value: spreadValue,
		});
	}

	if (overallSummary.discardedBlankCount > 0) {
		rows.push({
			metric: "Blank Scanner Records Excluded",
			value: `${overallSummary.discardedBlankCount}`,
		});
	}

	return rows;
}

function buildVersionRows(normalizedAnalyses) {
	return normalizedAnalyses.map((entry) => ({
		version: `V${entry.version}`,
		meanScore: `${formatFixed(entry.meanTotal, 1)}${
			Number.isFinite(entry.kEffective) && entry.kEffective > 0 ? `/${Number(entry.kEffective)}` : ""
		}`,
		students: `${entry.studentCount}`,
		kr20: entry.kr20 == null ? "N/A" : formatFixed(entry.kr20, 3),
	}));
}

function buildItemCheckRows(itemFlags) {
	if (!itemFlags?.hasData) return [];

	return [
		{
			check: `Negative ${itemFlags.pbLabel}`,
			threshold: "Below 0.00",
			count: `${itemFlags.negative.length}`,
			examples:
				joinExamples(
					itemFlags.negative,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} ${formatFixed(
							item.discrimination,
							2,
						)}`,
					3,
				) || "None flagged",
		},
		{
			check: `Weak ${itemFlags.pbLabel}`,
			threshold: `0.00 to below ${formatFixed(
				ITEM_ANALYSIS_THRESHOLDS.weakDiscrimination,
				2,
			)}`,
			count: `${itemFlags.weak.length}`,
			examples:
				joinExamples(
					itemFlags.weak,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} ${formatFixed(
							item.discrimination,
							2,
						)}`,
					3,
				) || "None flagged",
		},
		{
			check: "Very Hard Items",
			threshold: `${formatPercent(ITEM_ANALYSIS_THRESHOLDS.tooHard, 0)} or less correct`,
			count: `${itemFlags.hard.length}`,
			examples:
				joinExamples(
					itemFlags.hard,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} ${formatPercent(
							item.difficulty,
							0,
						)}`,
					3,
				) || "None flagged",
		},
		{
			check: "Very Easy Items",
			threshold: `${formatPercent(ITEM_ANALYSIS_THRESHOLDS.tooEasy, 0)} or more correct`,
			count: `${itemFlags.easy.length}`,
			examples:
				joinExamples(
					itemFlags.easy,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} ${formatPercent(
							item.difficulty,
							0,
						)}`,
					3,
				) || "None flagged",
		},
		{
			check: "Blank or Invalid Responses",
			threshold: `More than ${formatPercent(
				ITEM_ANALYSIS_THRESHOLDS.highBlankRate,
				0,
			)}`,
			count: `${itemFlags.blank.length}`,
			examples:
				joinExamples(
					itemFlags.blank,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} ${formatPercent(
							item.blankRate,
							0,
						)}`,
					3,
				) || "None flagged",
		},
		{
			check: "Positive Distractors",
			threshold: `DP above ${formatFixed(
				ITEM_ANALYSIS_THRESHOLDS.positiveDistractorDp,
				2,
			)}`,
			count: `${itemFlags.distractor.length}`,
			examples:
				joinExamples(
					itemFlags.distractor,
					(item) =>
						`${buildQuestionLabel(item, itemFlags.includeVersion)} option ${item.option} (DP ${formatFixed(
							item.dp,
							2,
						)})`,
					3,
				) || "None flagged",
		},
	];
}

function buildMatchingRows({
	students = [],
	gradeWithoutMatching = false,
	hasClassIds = false,
} = {}) {
	const totalStudents = Array.isArray(students) ? students.length : 0;
	if (!totalStudents) return [];

	const anyMatchingData = hasAnyMatchingData(students);

	if (!anyMatchingData) {
		if (gradeWithoutMatching || !hasClassIds) {
			return [
				{
					check: "Matching Status",
					value: "Class-list matching was skipped for this export.",
				},
			];
		}
		return [];
	}

	const counts = {
		exact: 0,
		close: 0,
		manual: 0,
		cannot: 0,
		unresolved: 0,
	};

	students.forEach((student) => {
		const attempted = String(student?.match_attempted || "").toUpperCase() === "Y";
		const matchType = String(student?.match_type || "").trim();
		if (!attempted) {
			counts.unresolved += 1;
			return;
		}
		if (MATCH_TYPE_GROUPS.exact.has(matchType)) {
			counts.exact += 1;
			return;
		}
		if (MATCH_TYPE_GROUPS.close.has(matchType)) {
			counts.close += 1;
			return;
		}
		if (MATCH_TYPE_GROUPS.cannot.has(matchType)) {
			counts.cannot += 1;
			return;
		}
		counts.manual += 1;
	});

	const matchedCount = counts.exact + counts.close + counts.manual;
	return [
		{
			check: "Matched Records",
			value: `${matchedCount}/${totalStudents}`,
		},
		{
			check: "Perfect Matches",
			value: `${counts.exact}`,
		},
		{
			check: "High-Confidence Matches",
			value: `${counts.close}`,
		},
		{
			check: "Manual Matches",
			value: `${counts.manual}`,
		},
		{
			check: "Non-Matchable Records",
			value: `${counts.cannot}`,
		},
		{
			check: "Unresolved Records",
			value: `${counts.unresolved}`,
		},
	];
}

function buildInsightSections({
	normalizedAnalyses = [],
	students = [],
	pointBiserialMode = "crpb",
	selectedQuestionCount = null,
	totalQuestionCount = null,
	gradeWithoutMatching = false,
	hasClassIds = false,
} = {}) {
	const sections = [];
	const overallSummary = buildOverallPerformanceSummary(normalizedAnalyses);
	const importantNoteRows = buildImportantNoteRows({
		normalizedAnalyses,
		students,
		gradeWithoutMatching,
		hasClassIds,
	});
	const overviewRows = buildOverviewRows({
		normalizedAnalyses,
		overallSummary,
		selectedQuestionCount,
		totalQuestionCount,
	});
	const versionRows = buildVersionRows(normalizedAnalyses);
	const itemCheckRows = buildItemCheckRows(
		collectItemFlags(normalizedAnalyses, pointBiserialMode),
	);
	const matchingRows = buildMatchingRows({
		students,
		gradeWithoutMatching,
		hasClassIds,
	});

	if (importantNoteRows.length) {
		sections.push({
			key: "important_notes",
			title: "Important Notes",
			columns: [
				{ key: "note", label: "Note" },
				{ key: "details", label: "Details" },
			],
			rows: importantNoteRows,
		});
	}

	if (overviewRows.length) {
		sections.push({
			key: "overview",
			title: "Overview",
			columns: [
				{ key: "metric", label: "Metric" },
				{ key: "value", label: "Value" },
			],
			rows: overviewRows,
		});
	}

	if (versionRows.length) {
		sections.push({
			key: "versions",
			title: "Version Breakdown",
			columns: [
				{ key: "version", label: "Version" },
				{ key: "meanScore", label: "Mean Score" },
				{ key: "students", label: "Students", align: "center" },
				{ key: "kr20", label: "KR-20" },
			],
			rows: versionRows,
		});
	}

	if (itemCheckRows.length) {
		sections.push({
			key: "item_checks",
			title: "Item Checks",
			columns: [
				{ key: "check", label: "Check" },
				{ key: "threshold", label: "Threshold" },
				{ key: "count", label: "Count", align: "center" },
				{ key: "examples", label: "Examples" },
			],
			rows: itemCheckRows,
		});
	}

	if (matchingRows.length) {
		sections.push({
			key: "matching",
			title: "Matching",
			columns: [
				{ key: "check", label: "Check" },
				{ key: "value", label: "Value" },
			],
			rows: matchingRows,
		});
	}

	return sections;
}

function buildInsightRowText(row, columns = []) {
	return columns
		.map((column) => {
			const value = String(row?.[column.key] || "").trim();
			if (!value) return "";
			return `${column.label}: ${value}`;
		})
		.filter(Boolean)
		.join("; ");
}

function buildInsightText(sections = []) {
	const filteredSections = (sections || []).filter(
		(section) => Array.isArray(section?.rows) && section.rows.length > 0,
	);
	if (!filteredSections.length) return "";

	return [
		"Processed Results Summary:",
		...filteredSections.flatMap((section) => [
			"",
			`${section.title}:`,
			...section.rows
				.map((row) => buildInsightRowText(row, section.columns))
				.filter(Boolean)
				.map((line) => `- ${line}`),
		]),
	].join("\n");
}

export function buildExamResultsEmailInsights({
	analysesByVersion = [],
	students = [],
	pointBiserialMode = "crpb",
	selectedQuestionCount = null,
	totalQuestionCount = null,
	gradeWithoutMatching = false,
	hasClassIds = false,
} = {}) {
	const normalizedAnalyses = normalizeAnalysesByVersion(analysesByVersion);
	const sections = buildInsightSections({
		normalizedAnalyses,
		students,
		pointBiserialMode,
		selectedQuestionCount,
		totalQuestionCount,
		gradeWithoutMatching,
		hasClassIds,
	});
	const lines = sections
		.flatMap((section) =>
			(section?.rows || []).map((row) => buildInsightRowText(row, section.columns)),
		)
		.filter(Boolean);
	const text = buildInsightText(sections);

	return {
		lines,
		text,
		sections,
		hasContent: sections.length > 0,
	};
}

export function inferEmailPreset(deliveryValue) {
	const normalized = String(deliveryValue || "")
		.trim()
		.toLowerCase();

	if (normalized.includes("courier")) return "courier";
	if (normalized.includes("campus")) return "campus_mail";
	if (normalized.includes("pick")) return "pickup";
	return "pickup";
}

export function getEmailPreset(presetValue) {
	return PRESET_MAP[presetValue] || PRESET_MAP.pickup;
}

export function buildExamResultsEmailDraft({
	presetValue,
	reqnum,
	instructorName,
	senderName,
} = {}) {
	const preset = getEmailPreset(presetValue);
	const resolvedSenderName = fallbackName(senderName);
	const greetingName = String(instructorName || "").trim() || "Instructor";
	const resolvedReqnum = String(reqnum || "").trim();
	const messageSections = [
		`Hello ${greetingName},`,
		"",
		`Your exam results are attached below for your review. ${preset.deliveryText}`,
		"",
		"If you plan to upload these grades to Canvas, please refer to the following article:",
		CANVAS_HELP_URL,
	];

	return {
		subject: `Exam Results${resolvedReqnum ? ` - ${resolvedReqnum}` : ""} (${preset.label})`,
		messageBody: messageSections.join("\n"),
		signature: `Best regards,\n${resolvedSenderName}`,
		subsignature: `Test Scoring & Questionnaire Services (TSQS)\nUniversity of Alberta`,
	};
}

function escapeHtml(value) {
	return String(value || "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

function linkifyPlainTextToHtml(value) {
	const escaped = escapeHtml(value);
	return escaped.replace(
		/(https?:\/\/[^\s<]+)/g,
		(url) =>
			`<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#1b5e20;text-decoration:underline;">${url}</a>`,
	);
}

function textToHtmlParagraphs(value) {
	return String(value || "")
		.split(/\n{2,}/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean)
		.map(
			(paragraph) =>
				`<p style="margin:0 0 16px;">${linkifyPlainTextToHtml(paragraph).replace(/\n/g, "<br/>")}</p>`,
		)
		.join("");
}

function textToCompactHtml(value) {
	return linkifyPlainTextToHtml(String(value || "").trim()).replace(/\n/g, "<br/>");
}

function buildInsightsSectionHtml(section, sectionIndex) {
	const columns = Array.isArray(section?.columns) ? section.columns : [];
	const rows = Array.isArray(section?.rows) ? section.rows : [];
	if (!columns.length || !rows.length) return "";

	const headerHtml = columns
		.map((column) => {
			const align = column?.align || "left";
			return `<th style="padding:10px 12px;border:1px solid #dbe5dd;background:#edf4ee;font-size:12px;font-weight:700;text-align:${align};color:#244b2c;">${escapeHtml(
				column.label,
			)}</th>`;
		})
		.join("");

	const rowsHtml = rows
		.map((row, rowIndex) => {
			const background = rowIndex % 2 === 0 ? "#ffffff" : "#f8fbf7";
			const cellsHtml = columns
				.map((column) => {
					const align = column?.align || "left";
					return `<td style="padding:10px 12px;border:1px solid #dbe5dd;background:${background};font-size:12px;line-height:1.5;text-align:${align};vertical-align:top;color:#16311d;">${linkifyPlainTextToHtml(
						String(row?.[column.key] || "N/A"),
					)}</td>`;
				})
				.join("");
			return `<tr>${cellsHtml}</tr>`;
		})
		.join("");

	return `<div style="margin-top:${sectionIndex === 0 ? 0 : 16}px;">
		<div style="margin:0 0 8px;font-size:13px;font-weight:700;color:#16311d;">${escapeHtml(
			section.title,
		)}</div>
		<table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;">
			<thead><tr>${headerHtml}</tr></thead>
			<tbody>${rowsHtml}</tbody>
		</table>
	</div>`;
}

function buildExamResultsEmailInsightsHtml(insights) {
	const sections = Array.isArray(insights?.sections)
		? insights.sections.filter((section) => Array.isArray(section?.rows) && section.rows.length > 0)
		: [];
	if (!sections.length) return "";

	return `<div style="margin:24px 0 20px;padding:18px;border:1px solid #dbe5dd;border-radius:14px;background:#f8fbf7;">
		<div style="margin:0 0 4px;font-size:16px;font-weight:700;color:#16311d;">Result Set Insights</div>
		<div style="margin:0 0 14px;font-size:12px;line-height:1.5;color:#4c5f52;">Automatically generated from the current scoring selection.</div>
		${sections.map((section, index) => buildInsightsSectionHtml(section, index)).join("")}
	</div>`;
}

export function buildExamResultsEmailText({
	messageBody,
	signature,
	subsignature,
	insights,
	includeInsights = true,
} = {}) {
	const insightsText = includeInsights ? String(insights?.text || "").trim() : "";
	const signatureBlock = [String(signature || "").trim(), String(subsignature || "").trim()]
		.filter(Boolean)
		.join("\n");

	return [
		String(messageBody || "").trim(),
		insightsText,
		signatureBlock,
	]
		.filter(Boolean)
		.join("\n\n");
}

export function buildExamResultsEmailHtml({
	messageBody,
	signature,
	subsignature,
	insights,
	includeInsights = true,
} = {}) {
	const bodyHtml = textToHtmlParagraphs(messageBody);
	const signatureHtml = textToCompactHtml(signature);
	const subsignatureHtml = textToCompactHtml(subsignature);
	const insightsHtml = includeInsights ? buildExamResultsEmailInsightsHtml(insights) : "";

	return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Exam Results</title>
  </head>
  <body style="margin:0;padding:24px;background:#f6f8f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#16311d;">
	<div style="width:100%;max-width:none;margin:0;background:#ffffff;border:1px solid #dbe5dd;border-radius:14px;overflow:hidden;box-sizing:border-box;">
      <div style="padding:24px 24px 12px;text-align:center;background:linear-gradient(180deg,#eef5ee 0%,#ffffff 100%);">
        <img src="cid:logo" alt="University of Alberta" style="max-height:56px;width:auto;" />
      </div>
      <div style="padding:0 24px 24px;font-size:15px;line-height:1.6;">
        ${bodyHtml}
        ${insightsHtml}
        <div style="padding-top:18px;">
		<div>
          ${signatureHtml}
		  <div style="font-size:12px;color:#111111;">${subsignatureHtml}</div>
		</div>
        </div>
		<div style="margin-top:24px;padding-top:18px;border-top:1px solid #dbe5dd;font-size:12px;line-height:1.5;color:#111111;">
		<strong>The University of Alberta respectfully acknowledges
that we are situated on Treaty 6 territory, traditional
lands of First Nations and Métis people.</strong>
</div>
		<div style="margin-top:4px;font-size:11px;line-height:1.5;color:#666666;">
		<strong>CONFIDENTIALITY NOTICE:</strong> This communication is intended for the use of the recipient to which it is addressed, and may contain confidential, personal and/or privileged information.  Please contact us immediately if you are not the intended recipient of this communication. If you are not the intended recipient of this communication, do not copy, distribute, or take action on it. Any communication received in error, or subsequent reply, should be deleted or destroyed.
		</div>
      </div>
    </div>
  </body>
</html>`;
}
